import React, {useState, useRef} from "react";
import {DocumentTextIcon} from "@heroicons/react/24/solid";
import SignatureCanvas from "react-signature-canvas";

import Button from "../ui-components/Button";
import Input from "../ui-components/Input";
import Modal from "../ui-components/Modal";
import {getPDF} from "../utils";
import RadioGroup from "../ui-components/RadioGroup";
import Label from "../ui-components/Label";


// Constants
const BUILDING_OWNER_TYPES = [
  'private_owner',
  'legal_representative'
]


const ReferencePendingApproval = ({
    intl,
    push,
    setApprovalConfirm,
    currentUserPendingApproval,
    countryApiRequest,
    approvals,
    setApprovals,
    reference,
    worksite
}) => {

  const signatureCanvasRef = useRef(null);

  const [sendingConfirm, setSendingConfirm] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [buildingOwnerType, setBuildingOwnerType] = useState(BUILDING_OWNER_TYPES[0]);
  const [buildingOwnerName, setBuildingOwnerName] = useState('');
  const [buildingOwnerCompany, setBuildingOwnerCompany] = useState('');
  const [buildingOwnerVAT, setBuildingOwnerVAT] = useState('');
  const [buildingOwnerEmail, setBuildingOwnerEmail] = useState('');
  const [signatureCanvasEmpty, setSignatureCanvasEmpty] = useState(true);
  const [approvalConfirmPdfGenerated, setApprovalConfirmPdfGenerated] = useState(false);

  const allRequiredFieldsFilled =
    currentUserPendingApproval.des_approval_type !== 'progettista' || (
      buildingOwnerType &&
      buildingOwnerName &&
      (
        buildingOwnerType !== 'legal_representative' ||
        buildingOwnerCompany
      ) &&
      buildingOwnerVAT &&
      buildingOwnerEmail &&
      !signatureCanvasEmpty
    )

  const generatePreview = async () => {
    setSendingPreview(true);
    setApprovalConfirmPdfGenerated(true)

    // build pdf generation payload
    const body = {
      owner_name: buildingOwnerName,
      owner_vat: buildingOwnerVAT,
      owner_signature: signatureCanvasRef.current.getTrimmedCanvas().toDataURL('image/png'),
      case_name: reference.content?.title ?? intl.formatMessage({id: 'untitled'}),
      worksite_city: `${worksite.city} (${worksite.cod_province})`,
      worksite_address: worksite.address,
      id_reference: currentUserPendingApproval.cod_reference_id,
      id_user_approval: currentUserPendingApproval.cod_approved_by,
      recreate: true,
    }
    if (buildingOwnerType === 'legal_representative')
      body['owner_company'] = buildingOwnerCompany;

    // generate liberatoria pdf and open it in a new tab
    getPDF(intl, push, countryApiRequest, '/reference/approval', body)
      .finally(() => {
        setSendingPreview(false)
      })
  }

  const confirmApproval = async () => {
    setSendingConfirm(true);
    try {
      // 1. update target approval (set approval timestamp)
      const approvalId = currentUserPendingApproval.id;
      const updatedApproval = await countryApiRequest.put(
        `/worksites_references_approvals/${approvalId}`,
        {
          ...currentUserPendingApproval,
          tms_approved_on: new Date()
        }
      )
      // 2. actions based on approval type
      switch (currentUserPendingApproval.des_approval_type) {
        case 'progettista':
          // send liberatoria to building owner via mail
          await countryApiRequest.post(
            '/worksites_references_approvals/send_liberatoria_progettista',
            {
              name: buildingOwnerName,
              email_address: buildingOwnerEmail,
              reference_id: reference.id,
              reference_name: reference.content?.title ?? intl.formatMessage({id: 'untitled'}),
              worksite_name: worksite.des_worksite,
              approver_id: currentUserPendingApproval.cod_approved_by,
              approver_name: currentUserPendingApproval.des_approved_by,
            }
          )
          break;
        case 'marketing':
          // the reference is going to be closed -> make it public!
          await countryApiRequest.put(
            `/worksites_references/${reference.id}`,
            {
              ...reference,
              r_perm: ['public']
            }
          )
          break;
        default:
          // do nothing
      }
      setApprovals(approvals.map((a) => {
        return updatedApproval.des_approval_type === a.des_approval_type && updatedApproval.cod_approved_by === a.cod_approved_by
          ? updatedApproval
          : a;
      }))
      push({type: 'success', title: intl.formatMessage({id: 'approval_successfully_updated'})});
    } catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})});
      throw error;
    } finally {
      setSendingConfirm(undefined)
    }
  }

  return (
    <Modal
      onExit={() => {setApprovalConfirm(undefined)}}
    >
      <h3 className="text-xl text-center mb-4">
        {intl.formatMessage({id: 'approve_reference'})}
      </h3>
      {
        currentUserPendingApproval.des_approval_type === 'progettista'
          // -------------------- CASE A: Progettista --------------------
          ? <>
            {/* ==================== Building owner info ==================== */}
            <div className="mb-4">
              <RadioGroup
                horizontal
                id="building-owner-type"
                title={intl.formatMessage({id: 'building_owner_type'})}
                options={BUILDING_OWNER_TYPES.map((bot) => ({
                  name: intl.formatMessage({id: bot}),
                  value: bot,
                }))}
                currentValue={buildingOwnerType}
                onChange={(selected) => { setBuildingOwnerType(selected) }}
              />
            </div>
            <Input
              full
              label={buildingOwnerType === 'legal_representative'
                ? intl.formatMessage({id: 'name_and_surname'})
                : intl.formatMessage({id: 'building_owner_name'})}
              value={buildingOwnerName}
              onChange={(e) => {
                setBuildingOwnerName(e.target.value)
              }}
              required
            />
            {
              buildingOwnerType === 'legal_representative' && (
                <Input
                  full
                  label={intl.formatMessage({id: 'building_owner_company'})}
                  value={buildingOwnerCompany}
                  onChange={(e) => {
                    setBuildingOwnerCompany(e.target.value)
                  }}
                  required
                />
              )
            }
            <Input
              full
              label={intl.formatMessage({id: 'vat_cf'})}
              value={buildingOwnerVAT}
              onChange={(e) => {
                setBuildingOwnerVAT(e.target.value)
              }}
              required
            />
            <Input
              full
              type="email"
              label={intl.formatMessage({id: 'building_owner_email'})}
              value={buildingOwnerEmail}
              onChange={(e) => {
                setBuildingOwnerEmail(e.target.value)
              }}
              required
            />
            {/* ==================== Building owner signature ==================== */}
            <Label>{intl.formatMessage({id: 'building_owner_signature'})}</Label>
            <div className="border rounded-xl my-2">
              <SignatureCanvas
                ref={(ref) => { signatureCanvasRef.current = ref; }}
                onEnd={() => { setSignatureCanvasEmpty(false); }}
                canvasProps={{width: 500, height: 200}}
              />
            </div>
            <div className="text-right my-1">
              <Button
                styleType="secondary"
                onClick={() => {
                  signatureCanvasRef.current.clear();
                  setSignatureCanvasEmpty(true);
                }}
              >
                {intl.formatMessage({id: 'reset'})}
              </Button>
            </div>
            {/* ==================== PDF generation ==================== */}
            <Button
              disabled={!allRequiredFieldsFilled}
              submitting={sendingPreview}
              paddingCls="py-2"
              className="mt-5 w-full"
              onClick={async () => {
                await generatePreview();
              }}
            >
              <DocumentTextIcon className="w-4 h-4"/>
              <span className="ml-1 mr-4">{intl.formatMessage({id: 'release_preview'})}</span>
            </Button>
          </>
          // -------------------- CASE B: CS/Marketing --------------------
          : <span>{intl.formatMessage({id: 'confirm_approval_dialog'})}</span>
      }
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
        <Button
          full
          styleType='empty'
          onClick={() => {
            setApprovalConfirm(undefined)
          }}
        >
          {intl.formatMessage({id: 'cancel'})}
        </Button>
        <Button
          full
          onClick={async () => {
            await confirmApproval();
          }}
          disabled={
            currentUserPendingApproval.des_approval_type === 'progettista' &&
            (!approvalConfirmPdfGenerated || !allRequiredFieldsFilled)
          }
          submitting={sendingConfirm}
        >
          {intl.formatMessage({id: 'confirm'})}
        </Button>
      </div>
    </Modal>
  )
}

export default ReferencePendingApproval;